import {
    DicomBase,
    GenerationModelDetails,
    ObjectStatus,
} from '~/domain/dicom/dicomBase';
import { RepeatedRoiNumberError } from '~/domain/errors/DomainError';
import { DicomProcessor } from '~/domain/services/dicomProcessor';
import { handleError } from '~/utils/errors';

/**
 * Why a file was not imported: it is not DICOM the application can read, or
 * it is a structure set that holds two ROIs under one ROI Number, which
 * leaves no way to tell which contour belongs to which structure.
 */
export const SKIP_REASONS = [
    'unimportable-dicom',
    'repeated-roi-number',
] as const;

export type SkipReason = (typeof SKIP_REASONS)[number];

export type SkipEntry = {
    reason: SkipReason;
    fileName: string;
};

export type SkipReport = {
    examined: number;
    skips: SkipEntry[];
};

export function createSkipReport(): SkipReport {
    return { examined: 0, skips: [] };
}

/**
 * Splits a file list into DICOM files and non-DICOM JSON files.
 *
 * The backend worker may return JSON files (optimization objectives, metadata,
 * etc.) alongside DICOM files. Passing JSON files to the DICOM anonymizer /
 * re-identifier causes it to throw and return null, silently dropping all
 * results. This function separates them at the source so only genuine DICOM
 * files reach the DICOM pipeline.
 */
export function splitDicomAndJsonFiles(files: File[]): {
    dicomFiles: File[];
    jsonFiles: File[];
} {
    const dicomFiles: File[] = [];
    const jsonFiles: File[] = [];
    for (const file of files) {
        if (file.name.toLowerCase().endsWith('.json')) {
            jsonFiles.push(file);
        } else {
            dicomFiles.push(file);
        }
    }
    return { dicomFiles, jsonFiles };
}

export function requestFileInput(
    onFileLoaded: (data: ArrayBuffer) => unknown,
    multiple: boolean
) {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = multiple;
    input.onchange = function () {
        const files = input.files;
        if (files) {
            for (let i = 0; i < files.length; ++i) {
                const file = files.item(i);
                if (file) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        if (reader.result) {
                            onFileLoaded(reader.result as ArrayBuffer);
                        }
                    };
                    reader.onerror = () => {
                        throw new Error(`FileHandler.requestFileInput faced an error reading file ${file.name}:\
                        ${reader.error}`);
                    };
                    reader.readAsArrayBuffer(file);
                }
            }
        }
    };
    input.click();
    input.remove();
}

/**
 * Reads a {@link File} and parses its contents as DICOM with {@link processFileContent}.
 * Archives are expected to be expanded by the caller.
 *
 * @param file {@link File} the contents of which are to be processed.
 */
export async function processFile(
    file: File,
    dicomProcessor: DicomProcessor,
    objectStatus?: ObjectStatus,
    model?: GenerationModelDetails,
    report?: SkipReport
): Promise<DicomBase | null> {
    try {
        const fileArrayBuffer = await file.arrayBuffer();
        const dicomData = processFileContent(
            dicomProcessor,
            fileArrayBuffer,
            file.name,
            report
        );
        if (dicomData && objectStatus) {
            dicomData.objectStatus = objectStatus;
        }
        if (dicomData && model) {
            dicomData.generationModel = model;
        }
        return dicomData;
    } catch (error) {
        handleError(
            error,
            `Error processing file '${file.name}': ${error}`,
            'processFileError'
        );
        return null;
    }
}

/**
 * Parses a file's contents as DICOM, whatever its name: stored datasets often
 * carry no extension, or one from the exporting system. The reader refuses
 * buffers that are not DICOM; a refused file is recorded in `report`.
 * @param arrayBuffer Contents of a {@link File} that is to be processed.
 * @param fileName The file's name, shown to the user when it is skipped.
 * @returns The parsed object, or null when the file was skipped.
 */
export function processFileContent(
    dicomProcessor: DicomProcessor,
    arrayBuffer: ArrayBuffer,
    fileName: string,
    report?: SkipReport
): DicomBase | null {
    if (report) {
        report.examined += 1;
    }
    try {
        const dicomData = dicomProcessor.processDicomData(arrayBuffer);
        if (!dicomData) {
            // Log only the reason, not the filename: names can be PHI
            // and are surfaced to the user via the SkipReport toast.
            console.warn('Skipping DICOM file (unsupported or unimportable)');
            report?.skips.push({ reason: 'unimportable-dicom', fileName });
        }
        return dicomData;
    } catch (error) {
        // Don't log the thrown error or filename: both can carry PHI
        // (buffer/identifying bits). The name reaches the user via the
        // SkipReport toast instead.
        const reason: SkipReason =
            error instanceof RepeatedRoiNumberError
                ? 'repeated-roi-number'
                : 'unimportable-dicom';
        console.warn(`Skipping file that could not be imported (${reason})`);
        report?.skips.push({ reason, fileName });
        return null;
    }
}
